import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight, Minus, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useSubscribeModal } from '../../context/SubscribeModalContext';
import {
  createAdEventId,
  getActiveAds,
  trackClick,
  trackDismissal,
  trackImpression,
} from '../../services/adService';
import SponsoredNotificationWidget from './SponsoredNotificationWidget';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 300;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgb(0 0 0 / 0.62);
`;

const Dialog = styled.div`
  position: relative;
  width: min(100%, 48rem);
  max-height: 92dvh;
  overflow: auto;
  padding: clamp(0.75rem, 2.5vw, 1.5rem);
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 1rem;
  background: ${props => props.theme.colors.card};
  color: ${props => props.theme.colors.cardForeground};
  box-shadow: 0 24px 70px rgb(0 0 0 / 0.35);

  @media (prefers-reduced-motion: no-preference) {
    animation: popup-enter 180ms ease-out;
    @keyframes popup-enter {
      from { opacity: 0; transform: translateY(0.5rem) scale(0.98); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.25rem;
  right: ${props => props.$right || '0.25rem'};
  z-index: 1;
  display: grid;
  place-items: center;
  width: 2.25rem;
  height: 2.25rem;
  border: 0;
  border-radius: 50%;
  background: rgb(0 0 0 / 0.72);
  color: white;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const Creative = styled.img`
  display: block;
  width: 100%;
  max-height: 68dvh;
  object-fit: contain;
  border-radius: 0.65rem;
`;

const SlideFrame = styled.div`
  position: relative;
`;

const SlideArrow = styled.button`
  position: absolute;
  top: 50%;
  ${props => props.$direction}: 0.75rem;
  z-index: 1;
  display: grid;
  place-items: center;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  transform: translateY(-50%);
  border: 1px solid rgb(255 255 255 / 0.65);
  border-radius: 50%;
  background: rgb(0 0 0 / 0.7);
  color: white;
  cursor: pointer;

  &:hover {
    background: rgb(0 0 0 / 0.88);
  }

  &:focus-visible {
    outline: 3px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const SlideTitle = styled.h2`
  margin: 0.9rem 0 0;
  color: ${props => props.theme.colors.foreground};
  font-size: clamp(1.05rem, 2vw, 1.35rem);
  line-height: 1.35;
  text-align: center;
`;

const SlideControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const SlideDots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const SlideDot = styled.button`
  width: 0.7rem;
  height: 0.7rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.border};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 3px;
  }
`;

const SlideCount = styled.span`
  color: ${props => props.theme.colors.mutedForeground};
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
`;

const Disclosure = styled.p`
  margin: 0.55rem 0 0.25rem;
  color: ${props => props.theme.colors.mutedForeground};
  font-size: 0.75rem;
  line-height: 1.4;
  text-align: center;
`;

const ActionLink = styled.a`
  display: block;
  border-radius: 0.65rem;
  line-height: 0;

  &:focus-visible {
    outline: 3px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const tenantSlug = (process.env.REACT_APP_TENANT_SLUG || 'default').trim().toLowerCase();
const popupSessionKey = `blogify-popup-ad-shown:${tenantSlug}`;
const notificationSessionKey = `blogify-notification-ad-dismissed:${tenantSlug}`;
const POPUP_DELAY_MS = 3000;
const AUTOPLAY_DELAY_MS = 3000;

const readSessionFlag = key => {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(window.sessionStorage.getItem(key));
  } catch (error) {
    console.error('Unable to read ad session state:', error);
    return false;
  }
};

export default function PopupAdController() {
  const { language } = useLanguage();
  const { isOpen: subscribeIsOpen } = useSubscribeModal();
  const { pathname } = useLocation();
  const [ads, setAds] = useState([]);
  const [notificationAds, setNotificationAds] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [popupClosed, setPopupClosed] = useState(false);
  const [popupShownInSession, setPopupShownInSession] = useState(() => readSessionFlag(popupSessionKey));
  const [notificationDismissed, setNotificationDismissed] = useState(() => readSessionFlag(notificationSessionKey));
  const [popupViewNumber, setPopupViewNumber] = useState(0);
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const trackedPopupViews = useRef(new Set());
  const dismissalTracked = useRef(new Set());
  const closePopupRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getActiveAds('popup', { language, limit: 10 }),
      getActiveAds('notification', { language, limit: 10 }),
    ]).then(([popupResponse, notificationResponse]) => {
      if (cancelled) return;
      const eligiblePopupAds = popupResponse?.success
        ? (popupResponse.data || []).filter((item) => item?.imageUrl && item?.clickUrl)
        : [];
      const eligibleNotificationAds = notificationResponse?.success
        ? (notificationResponse.data || []).filter((item) => item?.message?.trim() && item?.clickUrl)
        : [];
      setAds(eligiblePopupAds);
      setNotificationAds(eligibleNotificationAds);
      setActiveIndex(0);
    });

    return () => {
      cancelled = true;
    };
  }, [language]);

  const ad = ads[activeIndex] || ads[0] || null;
  const isVisible = Boolean(hasOpened && ad && !popupClosed && !subscribeIsOpen);

  useEffect(() => {
    if (
      !imageLoaded
      || !ad
      || popupShownInSession
      || popupClosed
      || subscribeIsOpen
    ) return undefined;

    let timerId;
    const showWhenUnobstructed = () => {
      const consent = document.querySelector('[data-cookie-consent]');
      const consentIsVisible = consent
        && consent.getClientRects().length > 0
        && window.getComputedStyle(consent).visibility !== 'hidden';
      if (consentIsVisible) {
        timerId = window.setTimeout(showWhenUnobstructed, 500);
        return;
      }

      setHasOpened(true);
      setPopupShownInSession(true);
      try {
        window.sessionStorage.setItem(popupSessionKey, '1');
      } catch (error) {
        console.error('Unable to persist popup session state:', error);
      }
    };

    timerId = window.setTimeout(showWhenUnobstructed, POPUP_DELAY_MS);
    return () => window.clearTimeout(timerId);
  }, [ad, imageLoaded, popupClosed, popupShownInSession, subscribeIsOpen]);

  useEffect(() => {
    if (!isVisible || !imageLoaded || ads.length < 2) return undefined;

    const timeoutId = window.setTimeout(() => {
      setActiveIndex((index) => (index + 1) % ads.length);
      setImageLoaded(false);
      setPopupViewNumber((value) => value + 1);
    }, AUTOPLAY_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [activeIndex, ads.length, imageLoaded, isVisible]);

  useEffect(() => {
    if (!isVisible || !imageLoaded || !ad?._id) return;
    const viewKey = `${ad._id}:${popupViewNumber}`;
    if (trackedPopupViews.current.has(viewKey)) return;
    trackedPopupViews.current.add(viewKey);
    trackImpression(ad._id, {
      eventId: createAdEventId(),
      language,
      pagePath: pathname,
    });
  }, [ad, imageLoaded, isVisible, language, pathname, popupViewNumber]);

  const closePopup = useCallback((shouldTrackDismissal = true) => {
    if (shouldTrackDismissal && ad?._id && !dismissalTracked.current.has(ad._id)) {
      dismissalTracked.current.add(ad._id);
      trackDismissal(ad._id, {
        eventId: createAdEventId(),
        language,
        pagePath: pathname,
      });
    }
    setPopupClosed(true);
  }, [ad, language, pathname]);
  closePopupRef.current = closePopup;

  const dismissNotification = useCallback((notificationAd) => {
    if (notificationAd?._id) {
      trackDismissal(notificationAd._id, {
        eventId: createAdEventId(),
        language,
        pagePath: pathname,
      });
    }
    setNotificationDismissed(true);
    try {
      window.sessionStorage.setItem(notificationSessionKey, '1');
    } catch (error) {
      console.error('Unable to persist notification session state:', error);
    }
  }, [language, pathname]);

  useEffect(() => {
    if (!isVisible) return undefined;
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePopupRef.current?.();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll('a[href], button:not([disabled])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [isVisible]);

  const changeSlide = (nextIndex) => {
    setActiveIndex((nextIndex + ads.length) % ads.length);
    setImageLoaded(false);
    setPopupViewNumber((value) => value + 1);
  };

  const notificationIsVisible = popupClosed
    && !isVisible
    && !notificationDismissed
    && !subscribeIsOpen;

  return (
    <>
      {!hasOpened && !popupShownInSession && ad?.imageUrl && (
        <img
          src={ad.imageUrl}
          alt=""
          aria-hidden="true"
          onLoad={() => setImageLoaded(true)}
          onError={() => {
            setImageLoaded(false);
            setAds(current => current.filter(item => item._id !== ad._id));
            setActiveIndex(0);
            console.error('Popup creative failed to load', { adId: ad._id });
          }}
          style={{ display: 'none' }}
        />
      )}
      {isVisible && ad && (
        <Overlay onClick={event => {
          if (event.target === event.currentTarget) closePopup();
        }}>
          <Dialog
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={`Advertisement carousel: ${ad.title || ad.name || 'Sponsored content'}`}
          >
            <CloseButton
              ref={closeButtonRef}
              type="button"
              onClick={() => closePopup()}
              aria-label="Close advertisement"
            >
              <X size={19} aria-hidden="true" />
            </CloseButton>
            <CloseButton
              $right="2.75rem"
              type="button"
              onClick={() => closePopup()}
              aria-label="Minimize advertisement"
            >
              <Minus size={19} aria-hidden="true" />
            </CloseButton>
            <SlideFrame aria-live="polite">
              <ActionLink
                href={ad.clickUrl}
                aria-label={ad.ctaText || `Open ${ad.title || ad.name || 'sponsored offer'}`}
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={() => {
                  trackClick(ad._id, {
                    eventId: createAdEventId(),
                    language,
                    pagePath: pathname,
                  });
                  closePopup(false);
                }}
              >
                <Creative
                  src={ad.imageUrl}
                  alt={ad.title || ad.name || 'Sponsored offer'}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => {
                    setImageLoaded(false);
                    setAds(current => current.filter(item => item._id !== ad._id));
                    setActiveIndex(0);
                    console.error('Popup creative failed to load', { adId: ad._id });
                  }}
                />
              </ActionLink>
              {ads.length > 1 && (
                <>
                  <SlideArrow
                    type="button"
                    $direction="left"
                    onClick={() => changeSlide(activeIndex - 1)}
                    aria-label="Previous advertisement"
                  >
                    <ChevronLeft size={24} aria-hidden="true" />
                  </SlideArrow>
                  <SlideArrow
                    type="button"
                    $direction="right"
                    onClick={() => changeSlide(activeIndex + 1)}
                    aria-label="Next advertisement"
                  >
                    <ChevronRight size={24} aria-hidden="true" />
                  </SlideArrow>
                </>
              )}
            </SlideFrame>
            <SlideTitle>{ad.title || ad.name}</SlideTitle>
            {ads.length > 1 && (
              <SlideControls aria-label="Advertisement carousel controls">
                <SlideDots>
                  {ads.map((item, index) => (
                    <SlideDot
                      key={item._id}
                      type="button"
                      $active={index === activeIndex}
                      onClick={() => changeSlide(index)}
                      aria-label={`Show advertisement ${index + 1}: ${item.title || item.name}`}
                      aria-current={index === activeIndex ? 'true' : undefined}
                    />
                  ))}
                </SlideDots>
                <SlideCount>{activeIndex + 1} / {ads.length}</SlideCount>
              </SlideControls>
            )}
            {ad.description && <Disclosure>{ad.description}</Disclosure>}
            <Disclosure>Advertisement · Affiliate promotion</Disclosure>
          </Dialog>
        </Overlay>
      )}
      <SponsoredNotificationWidget
        ads={notificationAds}
        isVisible={notificationIsVisible}
        language={language}
        pagePath={pathname}
        onDismiss={dismissNotification}
      />
    </>
  );
}
